import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { TextField } from '@vibe/core';
import { useTranslation } from '@/i18n/useTranslation';
import type { MapLegendEntry } from '@/lib/api/map';
import { useMapDesignationFilter } from '@/context/MapDesignationFilterContext';
import { filterLegendEntries } from '@/lib/map/map-legend-filter';
import MapDesignationFilterList from './MapDesignationFilterList';
import styles from './MapLegendPanel.module.css';

type Props = {
  open: boolean;
  layerName: string;
  entries: MapLegendEntry[];
  onClose: () => void;
};

export default function MapLegendPanel({ open, layerName, entries, onClose }: Props) {
  const t = useTranslation();
  const titleId = useId();
  const filterId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const [filterQuery, setFilterQuery] = useState('');
  const { enabledCount, selectAll, selectNone, selectTourismOnly } = useMapDesignationFilter();

  const filteredEntries = useMemo(
    () => filterLegendEntries(entries, filterQuery),
    [entries, filterQuery],
  );

  useEffect(() => {
    if (!open) {
      setFilterQuery('');
      return undefined;
    }
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const metaText = t.map.legend.mapVisibleMeta
    .replace('{shown}', String(enabledCount))
    .replace('{total}', String(entries.length));

  return (
    <div className={styles.root}>
      <button
        type="button"
        className={styles.backdrop}
        aria-label={t.map.legend.close}
        onClick={onClose}
      />
      <section
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="map-legend-panel"
      >
        <header className={styles.header}>
          <h2 id={titleId} className={styles.title}>
            {t.map.legend.mapFilterTitle.replace('{layer}', layerName)}
          </h2>
          <button
            ref={closeRef}
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            data-testid="map-legend-panel-close"
          >
            {t.map.legend.close}
          </button>
        </header>
        <div className={styles.controls}>
          <p className={styles.intro}>{t.map.legend.mapFilterIntro}</p>
          <div className={styles.presetRow}>
            <button
              type="button"
              className={styles.presetBtn}
              onClick={selectAll}
              data-testid="map-designation-select-all"
            >
              {t.map.legend.selectAll}
            </button>
            <button
              type="button"
              className={styles.presetBtn}
              onClick={selectTourismOnly}
              data-testid="map-designation-select-tourism"
            >
              {t.map.legend.selectTourism}
            </button>
            <button
              type="button"
              className={styles.presetBtn}
              onClick={selectNone}
              data-testid="map-designation-select-none"
            >
              {t.map.legend.selectNone}
            </button>
          </div>
          <div className={styles.filterRow}>
            <TextField
              id={filterId}
              title={t.map.legend.filterLabel}
              placeholder={t.map.legend.filterPlaceholder}
              value={filterQuery}
              onChange={setFilterQuery}
              size={TextField.sizes.MEDIUM}
              data-testid="map-legend-filter"
            />
          </div>
        </div>
        <p className={styles.meta}>{metaText}</p>
        <div className={styles.scroll}>
          <MapDesignationFilterList entries={filteredEntries} />
        </div>
      </section>
    </div>
  );
}
